import { useState, useRef, useEffect } from "react"
import {
  Box,
  IconButton,
  HStack,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react"
import { Icon } from "@chakra-ui/react"
import {
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaVolumeDown,
  FaMusic,
} from "react-icons/fa"

// Collapsed to a single music button in the top-right corner; the old
// bottom-right pill sat directly on top of the answer buttons on tablets.
// Tapping the music icon expands the playback/volume controls.
export const MusicPlayer = () => {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(0.5)
  const { isOpen: isExpanded, onToggle: toggleExpanded } = useDisclosure()

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume
    }
  }, [volume])

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause()
      } else {
        audioRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const handleVolumeUp = () => {
    setVolume(prev => Math.min(prev + 0.1, 1))
  }

  const handleVolumeDown = () => {
    setVolume(prev => Math.max(prev - 0.1, 0))
  }

  return (
    <Box
      position="fixed"
      top={2}
      right={2}
      zIndex={900}
      bg="blackAlpha.700"
      borderRadius="full"
      p={1}
      boxShadow="lg"
    >
      <audio ref={audioRef} loop>
        <source src="/counting-colors.mp3" type="audio/mpeg" />
      </audio>

      <HStack spacing={1}>
        <Tooltip
          label={isExpanded ? "Hide music controls" : "Music"}
          placement="bottom"
        >
          <IconButton
            aria-label={
              isExpanded ? "Hide music controls" : "Show music controls"
            }
            icon={<Icon as={FaMusic} />}
            onClick={toggleExpanded}
            size="sm"
            variant="ghost"
            colorScheme="whiteAlpha"
            color="white"
            _hover={{ bg: "whiteAlpha.300" }}
          />
        </Tooltip>

        {isExpanded && (
          <>
            <Tooltip label={isPlaying ? "Pause" : "Play"} placement="bottom">
              <IconButton
                aria-label={isPlaying ? "Pause music" : "Play music"}
                icon={<Icon as={isPlaying ? FaPause : FaPlay} />}
                onClick={togglePlay}
                size="sm"
                variant="ghost"
                colorScheme="whiteAlpha"
                color="white"
                _hover={{ bg: "whiteAlpha.300" }}
              />
            </Tooltip>

            <Tooltip label="Volume Down" placement="bottom">
              <IconButton
                aria-label="Volume down"
                icon={<Icon as={FaVolumeDown} />}
                onClick={handleVolumeDown}
                size="sm"
                variant="ghost"
                colorScheme="whiteAlpha"
                color="white"
                _hover={{ bg: "whiteAlpha.300" }}
              />
            </Tooltip>

            <Tooltip label="Volume Up" placement="bottom">
              <IconButton
                aria-label="Volume up"
                icon={<Icon as={FaVolumeUp} />}
                onClick={handleVolumeUp}
                size="sm"
                variant="ghost"
                colorScheme="whiteAlpha"
                color="white"
                _hover={{ bg: "whiteAlpha.300" }}
              />
            </Tooltip>
          </>
        )}
      </HStack>
    </Box>
  )
}
